import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AlertCircle, CheckCircle2, Zap } from 'lucide-react';

const ProblemSolutionSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const problems = [
    {
      icon: AlertCircle,
      title: "Unvollständige Datenbanken",
      description: "Andere Anbieter fokussieren sich nur auf Großstädte und lassen kleinere Gemeinden völlig aus."
    },
    {
      icon: AlertCircle,
      title: "Veraltete Kontaktdaten",
      description: "Viele Plattformen pflegen ihre Daten nicht regelmäßig – Sie verschwenden Zeit mit falschen Nummern."
    },
    {
      icon: AlertCircle,
      title: "Teure Abonnements",
      description: "Monatliche Gebühren für Daten, die Sie vielleicht gar nicht brauchen. Keine Flexibilität."
    },
    {
      icon: AlertCircle,
      title: "Komplizierte Filter",
      description: "Umständliche Suchfunktionen machen es schwer, genau die Leads zu finden, die Sie wirklich benötigen."
    }
  ];

  const solutions = [
    {
      icon: CheckCircle2,
      title: "100% Abdeckung",
      description: "Von Großstädten bis zu kleinen Gemeinden – wir erfassen jedes Unternehmen in jedem Bundesland."
    },
    {
      icon: CheckCircle2,
      title: "Aktuelle & geprüfte Daten",
      description: "Unsere Datenbank wird kontinuierlich aktualisiert. Sie erhalten nur valide Kontaktinformationen."
    },
    {
      icon: CheckCircle2,
      title: "Faire Credit-Preise",
      description: "Zahlen Sie nur für das, was Sie nutzen. Keine versteckten Kosten, keine Abos. Volle Kostenkontrolle."
    },
    {
      icon: CheckCircle2,
      title: "Intelligente Filter",
      description: "Filtern Sie präzise nach Bundesland, Stadt und Branche – in Sekunden zu Ihren Wunsch-Leads."
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Kennen Sie das Problem?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Die meisten B2B-Datenbanken versprechen viel, liefern aber zu wenig.
            <br />Adloc macht den Unterschied.
          </p>
        </motion.div>

        {/* Problems */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="h-px w-12 bg-red-500" />
            <h3 className="text-2xl font-bold text-red-600">Das Problem</h3>
            <div className="h-px w-12 bg-red-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="bg-red-50 border border-red-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <problem.icon className="w-10 h-10 text-red-600 mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-2">{problem.title}</h4>
                <p className="text-gray-700">{problem.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Divider with icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex justify-center my-16"
        >
          <div className="bg-gradient-to-br from-[#2835F7] to-[#1a25c9] p-4 rounded-full shadow-xl">
            <Zap className="w-12 h-12 text-white" />
          </div>
        </motion.div>

        {/* Solutions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="h-px w-12 bg-green-500" />
            <h3 className="text-2xl font-bold text-green-600">Die Lösung: Adloc</h3>
            <div className="h-px w-12 bg-green-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="bg-green-50 border border-green-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <solution.icon className="w-10 h-10 text-green-600 mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-2">{solution.title}</h4>
                <p className="text-gray-700">{solution.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSolutionSection;